import type { MaybeRef, Ref } from 'vue'

import type { EndpointQuery } from '@/types/utils'

export const TIMETABLE_SELECTABLE_FILTER_KEYS = [
  'participant_type',
  'level',
  'location',
  'teacher'
] as const

export type TimetableSelectableFilterKey =
  (typeof TIMETABLE_SELECTABLE_FILTER_KEYS)[number]

const TIMETABLE_NON_SELECTABLE_FILTER_KEYS = ['course'] as const

type TimetableNonSelectableFilterKey =
  (typeof TIMETABLE_NON_SELECTABLE_FILTER_KEYS)[number]

type TimetableFilterKey =
  | TimetableSelectableFilterKey
  | TimetableNonSelectableFilterKey

export type TimetableFilterValue = string | undefined

export type TimetableFilterDict<
  T extends TimetableFilterKey = TimetableSelectableFilterKey
> = Record<T, TimetableFilterValue>

type TimetableQueryConfig = {
  widgetId: string
  applyNonSelectableFilters: MaybeRef<boolean>
  pageSize: MaybeRef<number>
  filtersOverride: MaybeRef<Partial<TimetableFilterDict<TimetableFilterKey>>>
}

export function useTimetableQuery({
  pageSize = 6,
  widgetId = 'timetable',
  applyNonSelectableFilters = true,
  filtersOverride = {}
}: Partial<TimetableQueryConfig> = {}) {
  const route = useRoute()
  const router = useRouter()

  /**
   * UTILS
   * */
  const isNonSelectableKey = (
    key: string
  ): key is TimetableNonSelectableFilterKey =>
    TIMETABLE_NON_SELECTABLE_FILTER_KEYS.includes(
      key as TimetableNonSelectableFilterKey
    )
  const prefix = (value: TimetableFilterKey) => `${widgetId}_${value}`
  const unwrapRef = <T>(ref: Ref<T> | T): T => (isRef(ref) ? ref.value : ref)

  /**
   * FILTERS
   * */
  const getSelectableParam = (key: TimetableSelectableFilterKey) =>
    flatten(route.query[prefix(key)]) ?? undefined
  const getNonSelectableParam = (key: TimetableNonSelectableFilterKey) =>
    flatten(route.query[key]) ?? undefined

  const getParam = (key: TimetableFilterKey) => {
    if (isNonSelectableKey(key)) return getNonSelectableParam(key)

    return getSelectableParam(key)
  }

  const filters = computed(() => ({
    ...[
      ...TIMETABLE_SELECTABLE_FILTER_KEYS,
      ...(unwrapRef(applyNonSelectableFilters)
        ? TIMETABLE_NON_SELECTABLE_FILTER_KEYS
        : [])
    ].reduce(
      (acc, key) => ({ ...acc, [key]: getParam(key) }),
      {} as TimetableFilterDict<TimetableFilterKey>
    )
  }))

  const setFilter = (
    key: TimetableSelectableFilterKey,
    value: TimetableFilterValue
  ) => {
    router.push({
      query: {
        ...route.query,
        [prefix(key)]: value
      }
    })
    if (page.value > 1) resetPage()
  }

  const resetFilters = () => {
    router.push({
      query: Object.fromEntries(
        Object.entries(route.query).filter(([key]) => {
          const [keyWidgetId] = key.split('_')
          return keyWidgetId !== widgetId || isNonSelectableKey(key)
        })
      )
    })
    resetPage()
  }

  /**
   * PAGINATION
   * */
  const page = ref(1)
  const hasNextPage = ref(false)

  const setHasNextPage = (value: boolean) => {
    hasNextPage.value = value
  }

  const changePage = () => {
    if (hasNextPage.value) page.value += 1
  }

  const resetPage = () => {
    page.value = 1
  }

  return {
    query: computed<EndpointQuery<'publicPagesCourseAndEventsDatesList'>>(
      () => ({
        page: page.value,
        page_size: unwrapRef(pageSize),
        ...unwrapRef(filters),
        ...unwrapRef(filtersOverride)
      })
    ),

    filters,
    setFilter,
    resetFilters,

    page,
    changePage,
    hasNextPage,
    setHasNextPage
  }
}
